import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Pagination,
  Paper,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
  IconButton,
  Collapse,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  PhotoLibrary as PhotoLibraryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { GalleryService } from '../../services/galleryService';
import GalleryCard from '../../components/Gallery/GalleryCard';
import { surface, brand, ink, line } from '../../theme/tokens';
import Seo from '../../components/Common/Seo';

const panelSx = {
  p: { xs: 2, md: 2.5 },
  backgroundColor: surface.card,
  border: `1px solid ${line.subtle}`,
  borderRadius: 2,
};

const emptyStateSx = {
  textAlign: 'center',
  py: { xs: 6, md: 8 },
  px: { xs: 2, md: 4 },
  backgroundColor: surface.sunken,
  borderRadius: 2,
};

const GalleryPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [currentPage, setCurrentPage] = useState(1);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const itemsPerPage = isMobile ? 8 : 12;

  useEffect(() => {
    loadGalleries();
  }, []);

  const loadGalleries = async () => {
    setLoading(true);
    try {
      const response = await GalleryService.fetchPublicGalleries();
      const galleriesData = response.items || [];
      
      // Fetch first 3 images for each gallery for collage display
      const galleriesWithImages = await Promise.all(
        galleriesData.map(async (gallery) => {
          try {
            const galleryDetail = await GalleryService.fetchGallery(gallery.id);
            return {
              ...gallery,
              images: {
                items: galleryDetail.images?.items?.slice(0, 3) || []
              }
            };
          } catch (error) {
            console.error(`Error loading images for gallery ${gallery.id}:`, error);
            return {
              ...gallery,
              images: { items: [] }
            };
          }
        })
      );
      
      setGalleries(galleriesWithImages);
    } catch (error) {
      console.error('Error loading galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryClick = (galleryId) => {
    navigate(`/gallery/${galleryId}`);
  };

  const filteredAndSortedGalleries = galleries
    .filter(gallery => {
      const matchesSearch = gallery.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (gallery.description && gallery.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (gallery.location && gallery.location.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCategory = !categoryFilter || gallery.category === categoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'date':
          return new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt);
        case 'category':
          return a.category.localeCompare(b.category);
        default:
          return 0;
      }
    });

  const totalPages = Math.ceil(filteredAndSortedGalleries.length / itemsPerPage);
  const paginatedGalleries = filteredAndSortedGalleries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getCategoryLabel = (category) => {
    return GalleryService.getGalleryCategories()
      .find(cat => cat.value === category)?.label || category;
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: { xs: 2, md: 4 }, 
        px: { xs: 1, sm: 2, md: 3 } 
      }}
    >
      <Seo
        title="Farm Photos: Fruit Forest, Pond & Fields"
        description="Photographs from our organic farm in Birbhum, West Bengal — the fruit forest, vegetable garden, nursery, pond and ducks, through all the seasons."
        path="/gallery"
      />

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ 
        mb: { xs: 2, md: 3 },
        px: { xs: 1, md: 0 }
      }}>
        <Link 
          component={RouterLink} 
          to="/" 
          color="inherit"
          sx={{ 
            color: ink.secondary,
            fontSize: { xs: '0.875rem', md: '1rem' },
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline', color: ink.primary }
          }}
        >
          Home
        </Link>
        <Typography 
          color="text.primary"
          sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
        >
          Gallery
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: { xs: 4, md: 6 },
        px: { xs: 1, md: 0 }
      }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          sx={{ 
            color: ink.primary,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
            mb: { xs: 1.5, md: 2 }
          }}
        >
          Farm Gallery
        </Typography>
        <Box
          sx={{ width: 64, height: 3, mx: 'auto', mb: 3, borderRadius: 2, backgroundColor: brand.mustard }}
        />
        <Typography 
          variant="h6" 
          sx={{ 
            color: ink.secondary,
            fontWeight: 400,
            lineHeight: 1.7,
            maxWidth: 640, 
            mx: 'auto',
            fontSize: { xs: '1rem', md: '1.25rem' },
            px: { xs: 2, md: 0 }
          }}
        >
          Explore the beauty of our organic farm through seasons, from vibrant crops to peaceful moments in nature
        </Typography>
      </Box>

      {/* Mobile Search & Filter Toggle */}
      {isMobile ? (
        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          {/* Search Bar */}
          <Paper elevation={0} sx={{ ...panelSx, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search galleries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="medium"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: ink.muted }} />
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.9rem'
                }
              }}
            />
          </Paper>

          {/* Filter Toggle & Active Filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterIcon />}
              endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              sx={{ 
                fontSize: '0.875rem',
                py: 1
              }}
            >
              Filters
            </Button>
            
            {/* Active filter chips */}
            {categoryFilter && (
              <Chip
                label={getCategoryLabel(categoryFilter)}
                onDelete={() => setCategoryFilter('')}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {sortBy !== 'date' && (
              <Chip
                label={`Sort: ${sortBy === 'title' ? 'A-Z' : 'Category'}`}
                onDelete={() => setSortBy('date')}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Collapsible Filters */}
          <Collapse in={filtersExpanded}>
            <Paper elevation={0} sx={panelSx}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      label="Category"
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      sx={{ fontSize: '0.9rem' }}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {GalleryService.getGalleryCategories().map(category => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={8}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={sortBy}
                      label="Sort By"
                      onChange={(e) => setSortBy(e.target.value)}
                      sx={{ fontSize: '0.9rem' }}
                    >
                      <MenuItem value="date">Date (Newest)</MenuItem>
                      <MenuItem value="title">Title (A-Z)</MenuItem>
                      <MenuItem value="category">Category</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="medium"
                    onClick={() => {
                      setSearchTerm('');
                      setCategoryFilter('');
                      setSortBy('date');
                      setCurrentPage(1);
                    }}
                    sx={{ 
                      py: 1.75,
                      fontSize: '0.8rem'
                    }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Collapse>
        </Box>
      ) : (
        /* Desktop Filters */
        <Paper elevation={0} sx={{ ...panelSx, mb: { xs: 4, md: 5 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search galleries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: ink.muted }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {GalleryService.getGalleryCategories().map(category => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="date">Date (Newest)</MenuItem>
                  <MenuItem value="title">Title (A-Z)</MenuItem>
                  <MenuItem value="category">Category</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setSearchTerm('');
                    setCategoryFilter('');
                    setSortBy('date');
                    setCurrentPage(1);
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Gallery Grid */}
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          py: { xs: 3, md: 4 } 
        }}>
          <CircularProgress />
        </Box>
      ) : paginatedGalleries.length > 0 ? (
        <>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {paginatedGalleries.map(gallery => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                lg={3} 
                key={gallery.id}
              >
                <GalleryCard
                  gallery={gallery}
                  onClick={() => handleGalleryClick(gallery.id)}
                  variant="collage"
                  showImageCount={true}
                  showDate={true}
                  showLocation={false}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: { xs: 3, md: 4 },
              px: { xs: 1, md: 0 }
            }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, value) => setCurrentPage(value)}
                color="primary"
                size={isMobile ? "medium" : "large"}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={isMobile ? 1 : 2}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }
                }}
              />
            </Box>
          )}

          {/* Results info */}
          <Box sx={{ 
            textAlign: 'center', 
            mt: { xs: 1.5, md: 2 },
            px: { xs: 2, md: 0 }
          }}>
            <Typography 
              variant="body2" 
              sx={{ color: ink.muted, fontSize: { xs: '0.8rem', md: '0.875rem' } }}
            >
              Showing {paginatedGalleries.length} of {filteredAndSortedGalleries.length} galleries
              {searchTerm || categoryFilter ? ' (filtered)' : ''}
            </Typography>
          </Box>
        </>
      ) : galleries.length === 0 ? (
        <Box sx={emptyStateSx}>
          <PhotoLibraryIcon sx={{ 
            fontSize: { xs: 48, md: 64 }, 
            color: brand.brown,
            opacity: 0.6,
            mb: { xs: 1.5, md: 2 } 
          }} />
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ 
              color: ink.primary,
              fontWeight: 600,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              mb: 1
            }}
          >
            No galleries available
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: ink.secondary,
              fontSize: { xs: '0.9rem', md: '1rem' },
              px: { xs: 2, md: 0 }
            }}
          >
            Check back soon for beautiful photos from our farm!
          </Typography>
        </Box>
      ) : (
        <Box sx={emptyStateSx}>
          <PhotoLibraryIcon sx={{ 
            fontSize: { xs: 48, md: 64 }, 
            color: brand.brown,
            opacity: 0.6,
            mb: { xs: 1.5, md: 2 } 
          }} />
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ 
              color: ink.primary,
              fontWeight: 600,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              mb: 1
            }}
          >
            No galleries match your search
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: ink.secondary,
              mb: 3,
              fontSize: { xs: '0.9rem', md: '1rem' },
              px: { xs: 2, md: 0 }
            }}
          >
            Try adjusting your filters or search terms
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setSearchTerm('');
              setCategoryFilter('');
              setCurrentPage(1);
            }}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              px: { xs: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            Clear Filters
          </Button>
        </Box>
      )}

      {/* Categories Overview */}
      {!loading && galleries.length > 0 && (
        <Box sx={{ 
          mt: { xs: 5, md: 7 }, 
          p: { xs: 2.5, md: 3.5 }, 
          bgcolor: surface.card, 
          border: `1px solid ${line.subtle}`,
          borderRadius: 2,
          mx: { xs: 1, md: 0 }
        }}>
          <Typography 
            variant="h5" 
            component="h2"
            gutterBottom
            sx={{ 
              color: ink.primary,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              pl: 2,
              borderLeft: `4px solid ${brand.mustard}`,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              mb: 3
            }}
          >
            Explore by Category
          </Typography>
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {GalleryService.getGalleryCategories().map(category => {
              const count = galleries.filter(g => g.category === category.value).length;
              if (count === 0) return null;
              
              return (
                <Grid item xs={6} sm={4} md={3} key={category.value}>
                  <Button
                    variant={categoryFilter === category.value ? 'contained' : 'outlined'}
                    fullWidth
                    onClick={() => {
                      setCategoryFilter(category.value);
                      setCurrentPage(1);
                      if (isMobile) {
                        setFiltersExpanded(false);
                      }
                    }}
                    size={isMobile ? "medium" : "large"}
                    sx={{ 
                      justifyContent: 'space-between',
                      py: { xs: 1, md: 1.5 },
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      fontWeight: 600,
                      textAlign: 'left',
                      ...(categoryFilter === category.value
                        ? {}
                        : { color: ink.secondary, borderColor: line.default }),
                      '& span:last-of-type': {
                        color: categoryFilter === category.value ? 'inherit' : ink.muted,
                      },
                      '& span': {
                        fontSize: { xs: '0.8rem', md: '0.875rem' }
                      }
                    }}
                  >
                    <span>{category.label}</span>
                    <span>({count})</span>
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default GalleryPage;